import type { FileTreeSortComparator, FileTreeSortEntry } from '@pierre/trees';

/**
 * A folder note is a Markdown file whose basename (without `.md`) matches its
 * parent directory name — e.g. `notes/notes.md` inside `notes/`.
 */
export function isFolderNote(entry: FileTreeSortEntry): boolean {
  if (entry.isDirectory) {
    return false;
  }
  const parent = entry.segments.at(-2);
  if (!parent) {
    return false;
  }
  const stem = entry.basename.replace(/\.md$/i, '');
  return stem.localeCompare(parent, undefined, { sensitivity: 'base' }) === 0;
}

/**
 * Kind at a path segment: intermediate segments are directories (1). The leaf
 * segment is a directory only when the entry itself is one; otherwise a file
 * (0). Matches Pierre's default prepared-path comparator.
 */
function kindAtDepth(entry: FileTreeSortEntry, depth: number): 0 | 1 {
  if (depth !== entry.segments.length - 1) {
    return 1;
  }
  return entry.isDirectory ? 1 : 0;
}

function compareBasenames(left: string, right: string): number {
  return left.localeCompare(right, undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * Pierre applies `sort` as a global prepared-path comparator (not a sibling-only
 * hook). Walk segments like the default, keep directories before files at every
 * level, and only among sibling files pin folder notes above the rest.
 */
export const folderNotePinningSort: FileTreeSortComparator = (left, right) => {
  const sharedDepth = Math.min(left.segments.length, right.segments.length);

  for (let depth = 0; depth < sharedDepth; depth++) {
    const leftSegment = left.segments[depth];
    const rightSegment = right.segments[depth];
    if (leftSegment == null || rightSegment == null) {
      continue;
    }
    if (leftSegment === rightSegment) {
      continue;
    }

    const leftKind = kindAtDepth(left, depth);
    const rightKind = kindAtDepth(right, depth);
    if (leftKind !== rightKind) {
      return leftKind === 1 ? -1 : 1;
    }

    // Sibling files: folder notes before other documents, then name order.
    if (leftKind === 0) {
      const leftPinned = isFolderNote(left);
      const rightPinned = isFolderNote(right);
      if (leftPinned !== rightPinned) {
        return leftPinned ? -1 : 1;
      }
    }

    return compareBasenames(leftSegment, rightSegment);
  }

  if (left.segments.length !== right.segments.length) {
    return left.segments.length < right.segments.length ? -1 : 1;
  }
  if (left.isDirectory !== right.isDirectory) {
    return left.isDirectory ? -1 : 1;
  }
  return 0;
};
